'use client';

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Code,
  Divider,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
  IconLink,
  IconTag,
} from '@tabler/icons-react';
import classes from './dev.module.css';

// Codes are decimal strings end-to-end; documented range is 1..130,000,000,000.
const CODE_RE = /^[1-9][0-9]*$/;
const CODE_MIN = 1n;
const CODE_MAX = 130_000_000_000n;
// EchoPrint project document id (projects/{projectId}/activatedCodes/{code}).
const PROJECT_RE = /^[A-Za-z0-9_-]{1,128}$/;

// Seed placeholder for a fresh save. No personal information.
const PLACEHOLDER_SAVE = JSON.stringify(
  {
    playerName: 'Test Player',
    level: 3,
    inventory: ['bone', 'ball', 'hat'],
    checkpoint: 'meadow-2',
    stats: { coins: 120, playtimeMinutes: 42 },
  },
  null,
  2,
);

type Status = 'not_activated' | 'activated' | 'used';

// Shared project-tag contract (id is identity, name is display-only). The
// server resolves every code's batch/project tag and returns it on each
// response; untagged/legacy codes resolve to the Unassigned default.
type Tag = { id: string; name: string };

const UNASSIGNED_TAG_ID = 'unassigned';

function parseTag(value: unknown): Tag | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.id !== 'string' || typeof record.name !== 'string') return null;
  return { id: record.id, name: record.name };
}

type Outcome =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; status: Status }
  | { kind: 'error'; message: string };

// Per-action feedback: idle | loading | ok(message) | error(message).
type ActionState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; message: string }
  | { kind: 'error'; message: string };

const IDLE: ActionState = { kind: 'idle' };

function validateProject(project: string): string | null {
  if (project.length === 0) return 'Enter a project id.';
  if (!PROJECT_RE.test(project)) {
    return 'Letters, digits, dashes and underscores only.';
  }
  return null;
}

function validateCode(code: string): string | null {
  if (code.length === 0) return 'Enter a code.';
  if (!CODE_RE.test(code)) {
    return 'Digits only: no spaces, signs, or decimals.';
  }
  const value = BigInt(code);
  if (value < CODE_MIN || value > CODE_MAX) {
    return `Code must be between ${CODE_MIN} and ${CODE_MAX}.`;
  }
  return null;
}

// Parse a textarea into a plain JSON object, or return an error message.
function parseSaveJson(text: string): { ok: true; value: unknown } | { ok: false; message: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, message: 'Save data must be valid JSON.' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, message: 'Save data must be a JSON object.' };
  }
  return { ok: true, value: parsed };
}

const RESULT_META: Record<
  Status,
  { label: string; color: string; icon: React.ReactNode }
> = {
  not_activated: {
    label: 'Not activated',
    color: 'gray',
    icon: <IconCircleX size={18} />,
  },
  activated: {
    label: 'Activated',
    color: 'teal',
    icon: <IconCircleCheck size={18} />,
  },
  used: {
    label: 'Used',
    color: 'yellow',
    icon: <IconAlertTriangle size={18} />,
  },
};

const CARD_STYLE = {
  background: '#23262f',
  border: '1px solid rgba(255,255,255,0.06)',
};

// A small accessible status line reused by every action card.
function ActionFeedback({ state }: { state: ActionState }) {
  if (state.kind === 'ok') {
    return (
      <Alert
        variant="light"
        color="teal"
        icon={<IconCircleCheck size={18} />}
        title="Done"
        role="status"
        aria-live="polite"
      >
        {state.message}
      </Alert>
    );
  }
  if (state.kind === 'error') {
    return (
      <Alert
        variant="light"
        color="red"
        icon={<IconAlertTriangle size={18} />}
        title="Error"
        role="alert"
      >
        {state.message}
      </Alert>
    );
  }
  return null;
}

// Renders the resolved project tag: display name plus the stable id, and a hint
// when the code is untagged/legacy (resolved to Unassigned).
function TagLine({ tag }: { tag: Tag }) {
  const isDefault = tag.id === UNASSIGNED_TAG_ID;
  return (
    <Group gap="xs" data-testid="tag-line">
      <IconTag size={16} />
      <Text size="sm" c="#e6e6e6">
        Tag: <strong>{tag.name}</strong> (
        <Code>{tag.id}</Code>)
      </Text>
      {isDefault ? (
        <Text size="xs" c="#8a8a8a">
          untagged / legacy
        </Text>
      ) : null}
    </Group>
  );
}

export default function DevPageClient() {
  const [project, setProject] = useState('');
  const [code, setCode] = useState('');
  const [projectError, setProjectError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  // Most recently resolved project tag for the checked code (from any action).
  const [tag, setTag] = useState<Tag | null>(null);

  // Use-code (create save) section.
  const [useJson, setUseJson] = useState(PLACEHOLDER_SAVE);
  const [useState_, setUseState] = useState<ActionState>(IDLE);

  // Save (retrieve + update) section.
  const [saveJson, setSaveJson] = useState('');
  const [linkedCodes, setLinkedCodes] = useState<string[] | null>(null);
  const [retrieveState, setRetrieveState] = useState<ActionState>(IDLE);
  const [updateState, setUpdateState] = useState<ActionState>(IDLE);

  // Link section.
  const [newCode, setNewCode] = useState('');
  const [newCodeError, setNewCodeError] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<ActionState>(IDLE);

  const loading = outcome.kind === 'loading';

  // Validate the shared project + current code inputs; returns them trimmed or null.
  const requireProjectAndCode = (): { project: string; code: string } | null => {
    const trimmedProject = project.trim();
    const projectValidationError = validateProject(trimmedProject);
    const codeValidationError = validateCode(code);
    setProjectError(projectValidationError);
    setCodeError(codeValidationError);
    if (projectValidationError || codeValidationError) return null;
    return { project: trimmedProject, code };
  };

  const check = async () => {
    const inputs = requireProjectAndCode();
    if (!inputs) {
      setOutcome({ kind: 'idle' });
      return;
    }

    setOutcome({ kind: 'loading' });
    try {
      const res = await fetch('/api/codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs),
      });

      const data = (await res.json().catch(() => null)) as
        | { status?: Status; tag?: unknown; error?: string }
        | null;

      if (!res.ok) {
        setOutcome({
          kind: 'error',
          message: data?.error ?? 'Something went wrong. Please try again.',
        });
        return;
      }

      if (data?.status) {
        setTag(parseTag(data.tag));
        setOutcome({ kind: 'result', status: data.status });
      } else {
        setOutcome({
          kind: 'error',
          message: 'Unexpected response. Please try again.',
        });
      }
    } catch {
      setOutcome({ kind: 'error', message: 'Network error. Please try again.' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void check();
  };

  // USE: create a save for the currently-checked activated code.
  const doUse = async () => {
    const inputs = requireProjectAndCode();
    if (!inputs) return;
    const parsed = parseSaveJson(useJson);
    if (!parsed.ok) {
      setUseState({ kind: 'error', message: parsed.message });
      return;
    }

    setUseState({ kind: 'loading' });
    try {
      const res = await fetch('/api/codes/use', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inputs, data: parsed.value }),
      });
      const data = (await res.json().catch(() => null)) as
        | { status?: string; tag?: unknown; error?: string }
        | null;
      if (!res.ok) {
        setUseState({
          kind: 'error',
          message: data?.error ?? 'Could not create the save.',
        });
        return;
      }
      setTag(parseTag(data?.tag));
      setUseState({ kind: 'ok', message: 'Save created. This code is now used.' });
      setOutcome({ kind: 'result', status: 'used' });
    } catch {
      setUseState({ kind: 'error', message: 'Network error. Please try again.' });
    }
  };

  // GET SAVE: fetch the save for the current used code + its linked codes.
  const doRetrieve = async () => {
    const inputs = requireProjectAndCode();
    if (!inputs) return;

    setRetrieveState({ kind: 'loading' });
    setUpdateState(IDLE);
    try {
      const res = await fetch('/api/codes/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs),
      });
      const data = (await res.json().catch(() => null)) as
        | { data?: unknown; linkedCodes?: string[]; tag?: unknown; error?: string }
        | null;
      if (!res.ok) {
        setRetrieveState({
          kind: 'error',
          message: data?.error ?? 'Could not load the save.',
        });
        return;
      }
      setSaveJson(JSON.stringify(data?.data ?? {}, null, 2));
      setLinkedCodes(Array.isArray(data?.linkedCodes) ? data!.linkedCodes : []);
      setTag(parseTag(data?.tag));
      setRetrieveState({ kind: 'ok', message: 'Save loaded.' });
    } catch {
      setRetrieveState({ kind: 'error', message: 'Network error. Please try again.' });
    }
  };

  // UPDATE SAVE: write the edited JSON back to the current used code's save.
  const doUpdate = async () => {
    const inputs = requireProjectAndCode();
    if (!inputs) return;
    const parsed = parseSaveJson(saveJson);
    if (!parsed.ok) {
      setUpdateState({ kind: 'error', message: parsed.message });
      return;
    }

    setUpdateState({ kind: 'loading' });
    try {
      const res = await fetch('/api/codes/save', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inputs, data: parsed.value }),
      });
      const data = (await res.json().catch(() => null)) as
        | { status?: string; tag?: unknown; error?: string }
        | null;
      if (!res.ok) {
        setUpdateState({
          kind: 'error',
          message: data?.error ?? 'Could not save changes.',
        });
        return;
      }
      setTag(parseTag(data?.tag));
      setUpdateState({ kind: 'ok', message: 'Changes saved.' });
    } catch {
      setUpdateState({ kind: 'error', message: 'Network error. Please try again.' });
    }
  };

  // LINK: attach a second activated-unused code to the current used code's save.
  const doLink = async () => {
    const trimmedProject = project.trim();
    const projectValidationError = validateProject(trimmedProject);
    const usedValidationError = validateCode(code);
    const newValidationError = validateCode(newCode);
    setProjectError(projectValidationError);
    setCodeError(usedValidationError);
    setNewCodeError(newValidationError);
    if (projectValidationError || usedValidationError || newValidationError) return;

    setLinkState({ kind: 'loading' });
    try {
      const res = await fetch('/api/codes/link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project: trimmedProject,
          usedCode: code,
          newCode,
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { status?: string; linkedCodes?: string[]; tag?: unknown; error?: string }
        | null;
      if (!res.ok) {
        setLinkState({
          kind: 'error',
          message: data?.error ?? 'Could not link the codes.',
        });
        return;
      }
      const linked = Array.isArray(data?.linkedCodes) ? data!.linkedCodes : [];
      setLinkedCodes(linked);
      const linkedTag = parseTag(data?.tag);
      setLinkState({
        kind: 'ok',
        message: linkedTag
          ? `Linked. ${linked.length} code(s) now share this save. New code tag: ${linkedTag.name} (${linkedTag.id}).`
          : `Linked. ${linked.length} code(s) now share this save.`,
      });
    } catch {
      setLinkState({ kind: 'error', message: 'Network error. Please try again.' });
    }
  };

  const status = outcome.kind === 'result' ? outcome.status : null;

  return (
    // A <section> (not <main>) because AppShellMain already renders the page's
    // <main>; nesting <main> in <main> is invalid and can trigger DOM reparenting
    // and hydration mismatches. Styles live in a CSS module for a deterministic,
    // server/client-identical className.
    <section aria-label="Developer tools" className={classes.devScroll}>
      <Stack gap="lg" maw={560}>
        <div>
          <Title order={2} c="#ffffff">
            Dev
          </Title>
          <Text size="sm" c="#b8b8b8">
            Check a sticker code, then use, retrieve, link, or edit its save.
            Every result shows the code&apos;s resolved project tag; a code whose
            batch carries a tag shows that tag&apos;s name/id, while an untagged or
            legacy code resolves to the Unassigned default.
          </Text>
        </div>

        <Card radius="md" padding="lg" style={CARD_STYLE}>
          <form onSubmit={handleSubmit} noValidate>
            <Stack gap="md">
              <TextInput
                label="Project ID"
                placeholder="e.g. puppy-..."
                description="EchoPrint project id (projects/{projectId}/activatedCodes)."
                value={project}
                onChange={(e) => {
                  setProject(e.currentTarget.value);
                  if (projectError) setProjectError(null);
                }}
                error={projectError}
                disabled={loading}
                autoComplete="off"
              />

              <TextInput
                label="Code"
                placeholder="e.g. 12345"
                description="Decimal sticker code (1 to 130,000,000,000)."
                value={code}
                onChange={(e) => {
                  setCode(e.currentTarget.value);
                  if (codeError) setCodeError(null);
                }}
                error={codeError}
                disabled={loading}
                inputMode="numeric"
                autoComplete="off"
                data-autofocus
              />

              <Button type="submit" loading={loading} disabled={loading}>
                Check
              </Button>
            </Stack>
          </form>
        </Card>

        {outcome.kind === 'idle' && !codeError && !projectError ? (
          <Alert
            variant="light"
            color="blue"
            icon={<IconInfoCircle size={18} />}
            title="Ready"
            role="status"
          >
            Enter a project id, type a code, and press Check.
          </Alert>
        ) : null}

        {status
          ? (() => {
              const meta = RESULT_META[status];
              return (
                <Alert
                  variant="light"
                  color={meta.color}
                  icon={meta.icon}
                  title={meta.label}
                  role="status"
                  aria-live="polite"
                >
                  <Stack gap="xs" align="flex-start">
                    <Text size="sm">Status: {meta.label}</Text>
                    {tag ? <TagLine tag={tag} /> : null}
                  </Stack>
                </Alert>
              );
            })()
          : null}

        {outcome.kind === 'error' ? (
          <Alert
            variant="light"
            color="red"
            icon={<IconAlertTriangle size={18} />}
            title="Error"
            role="alert"
          >
            <Stack gap="xs" align="flex-start">
              <Text size="sm">{outcome.message}</Text>
              <Button size="xs" variant="light" color="red" onClick={() => void check()}>
                Retry
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {/* USE: only meaningful for an activated-unused code. */}
        {status === 'activated' ? (
          <Card radius="md" padding="lg" style={CARD_STYLE}>
            <Stack gap="md">
              <div>
                <Title order={4} c="#ffffff">
                  Create save (use code)
                </Title>
                <Text size="sm" c="#b8b8b8">
                  Creates one save for the checked code and marks it used.
                </Text>
              </div>
              <Textarea
                label="Save data (JSON)"
                description="A JSON object. Nested values are preserved."
                value={useJson}
                onChange={(e) => {
                  setUseJson(e.currentTarget.value);
                  if (useState_.kind === 'error') setUseState(IDLE);
                }}
                error={useState_.kind === 'error' ? useState_.message : null}
                autosize
                minRows={6}
                maxRows={16}
                disabled={useState_.kind === 'loading'}
                styles={{ input: { fontFamily: 'monospace' } }}
              />
              <Group>
                <Button
                  onClick={() => void doUse()}
                  loading={useState_.kind === 'loading'}
                  disabled={useState_.kind === 'loading'}
                >
                  Create save
                </Button>
              </Group>
              {useState_.kind === 'ok' ? <ActionFeedback state={useState_} /> : null}
            </Stack>
          </Card>
        ) : null}

        {/* RETRIEVE + UPDATE: for an already-used code. */}
        {status === 'used' ? (
          <Card radius="md" padding="lg" style={CARD_STYLE}>
            <Stack gap="md">
              <div>
                <Title order={4} c="#ffffff">
                  Save game
                </Title>
                <Text size="sm" c="#b8b8b8">
                  Retrieve this code&apos;s save, edit the JSON, and save changes.
                </Text>
              </div>

              <Group>
                <Button
                  variant="light"
                  onClick={() => void doRetrieve()}
                  loading={retrieveState.kind === 'loading'}
                  disabled={retrieveState.kind === 'loading'}
                >
                  Retrieve save
                </Button>
              </Group>

              <ActionFeedback state={retrieveState.kind === 'error' ? retrieveState : IDLE} />

              {linkedCodes !== null ? (
                <div>
                  <Text size="sm" c="#b8b8b8" mb={4}>
                    Linked codes ({linkedCodes.length}):
                  </Text>
                  <Group gap="xs">
                    {linkedCodes.length === 0 ? (
                      <Text size="sm" c="#8a8a8a">
                        None
                      </Text>
                    ) : (
                      linkedCodes.map((c) => (
                        <Code key={c}>{c}</Code>
                      ))
                    )}
                  </Group>
                </div>
              ) : null}

              <Textarea
                label="Save data (JSON)"
                description="Edit the retrieved JSON, then Save changes."
                value={saveJson}
                onChange={(e) => {
                  setSaveJson(e.currentTarget.value);
                  if (updateState.kind === 'error') setUpdateState(IDLE);
                }}
                error={updateState.kind === 'error' ? updateState.message : null}
                autosize
                minRows={6}
                maxRows={16}
                disabled={updateState.kind === 'loading'}
                styles={{ input: { fontFamily: 'monospace' } }}
              />
              <Group>
                <Button
                  onClick={() => void doUpdate()}
                  loading={updateState.kind === 'loading'}
                  disabled={updateState.kind === 'loading' || saveJson.length === 0}
                >
                  Save changes
                </Button>
              </Group>
              {updateState.kind === 'ok' ? <ActionFeedback state={updateState} /> : null}
            </Stack>
          </Card>
        ) : null}

        {/* LINK: attach another activated code to the current used code's save. */}
        {status === 'used' ? (
          <Card radius="md" padding="lg" style={CARD_STYLE}>
            <Stack gap="md">
              <div>
                <Title order={4} c="#ffffff">
                  Link another code
                </Title>
                <Text size="sm" c="#b8b8b8">
                  Attach a second activated code to this save so both resolve to it.
                </Text>
              </div>

              <TextInput
                label="Used code"
                description="The code that already owns the save (defaults to the checked code)."
                value={code}
                onChange={(e) => {
                  setCode(e.currentTarget.value);
                  if (codeError) setCodeError(null);
                }}
                error={codeError}
                disabled={linkState.kind === 'loading'}
                inputMode="numeric"
                autoComplete="off"
              />
              <TextInput
                label="New code"
                description="An activated-unused code to attach."
                value={newCode}
                onChange={(e) => {
                  setNewCode(e.currentTarget.value);
                  if (newCodeError) setNewCodeError(null);
                }}
                error={newCodeError}
                disabled={linkState.kind === 'loading'}
                inputMode="numeric"
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void doLink();
                  }
                }}
              />
              <Group>
                <Button
                  leftSection={<IconLink size={16} />}
                  onClick={() => void doLink()}
                  loading={linkState.kind === 'loading'}
                  disabled={linkState.kind === 'loading'}
                >
                  Link codes
                </Button>
              </Group>
              <ActionFeedback state={linkState} />
            </Stack>

            <Divider my="md" color="rgba(255,255,255,0.06)" />
            <Text size="xs" c="#8a8a8a">
              Linking is transactional and idempotent when the new code already points at
              this same save.
            </Text>
          </Card>
        ) : null}
      </Stack>
    </section>
  );
}
